// Sites.js
import React, { useState, useEffect, useCallback } from 'react';
import ViewOnlyNotice from './ViewOnlyNotice';
import ConfirmationDialog from './components/ConfirmationDialog';
import axios from 'axios';
import moment from 'moment-timezone'; // Import moment-timezone
import { getFormattedTimezones } from '../utils/timezoneUtils'; // Import timezone utility
import {
  Typography, Box, Paper, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button,
  Select, MenuItem, FormControl, InputLabel, Grid, Chip, Checkbox, FormControlLabel, FormGroup, TablePagination
} from '@mui/material'; // Added Checkbox, FormControlLabel, FormGroup, TablePagination
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
// Removed GoogleMapReact import
// Import Leaflet
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
// Removed manual map utils import, JobMapComponent handles it
import { useUser } from '../context/UserContext'; // Corrected import path
import { hasPermission } from '../utils/permissionStore'; // Import hasPermission from permissionStore
import { JobMapComponent } from '../components/JobMapComponent'; // Import JobMapComponent
import CreateFacilityDialog from './components/CreateFacilityDialog';
import ChangeFacilityStatusDialog from './components/ChangeFacilityStatusDialog'; // Import status dialog
import { showGlobalNotification } from '../context/NotificationContext.js';

// Removed GoogleMapReact specific MapMarker component

function Sites({ sites, onSearch, page, rowsPerPage, totalCount, onPaginationChange, readOnly = false }) {
  const { currentUser: userProfile } = useUser(); // Use useUser hook, alias currentUser
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false); // State for status dialog
  const [selectedSite, setSelectedSite] = useState(null); // Used for Edit and Status dialogs
  // Removed displayedSites state

  // State for filters
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  // Add statusFilter to the filters state
  const [filters, setFilters] = useState({ vendorFilter: '', companyFilter: '', city: '', state: '', country: '', statusFilter: '' });
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Removed map state/refs for Edit Dialog, JobMapComponent manages its own state

  // Determine user capabilities using central permissions
  const canUseAdvancedFilters = hasPermission('canUseSiteFilters');
  const canEditDelete = !readOnly && hasPermission('canManageSites');
  const canChangeStatus = !readOnly && hasPermission('canChangeSiteStatus');
  const canCreate = !readOnly && hasPermission('canCreateSites');

  // Memoize locationData for JobMapComponent to prevent unnecessary re-renders
  const memoizedLocationData = React.useMemo(() => {
    if (!selectedSite) return null;
    
    // Construct locationData for the map from selectedSite details
    const siteLocationData = {
      street: selectedSite.address1,
      // street2: selectedSite.address2, // Map component doesn't use street2 for geocoding
      city: selectedSite.city,
      state: selectedSite.state,
      zip: selectedSite.zip,
      // country: selectedSite.country, // Add if country field exists
      // Add coordinates if they exist directly on the site object (adjust path if needed)
      location: selectedSite.location // Assuming site.location has { type: 'Point', coordinates: [lng, lat] }
    };
    return siteLocationData;
  }, [selectedSite?.address1, selectedSite?.city, selectedSite?.state, selectedSite?.zip, selectedSite?.location]);

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  // Removed useEffect for displayedSites

  // Determine user capabilities based on profile - REMOVED, using hasPermission now
  // const canUseAdvancedFilters = userProfile && (['OW', 'sOW', 'RM'].includes(userProfile.primaryRole) || (userProfile.primaryRole === 'N/A' && userProfile.secondaryRoles?.admin));
  // const canEditDelete = userProfile?.primaryRole !== 'SP';
  // const canChangeStatus = canUseAdvancedFilters; // Align status change with advanced filter capability

  // Fetch Map Key and Filter Dropdown Data
  useEffect(() => {
    const headers = getAuthHeader();
    if (!headers) return;

    // Removed fetchMapKey call

    if (canUseAdvancedFilters) {
      setLoadingFilters(true);
      Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/users/vendor-list`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/facilities/distinct/cities`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/facilities/distinct/states`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/facilities/distinct/countries`, { headers })
      ]).then(([vendorRes, cityRes, stateRes, countryRes]) => {
        setVendors(vendorRes.data || []);
        const uniqueCompanyNames = [...new Set((vendorRes.data || []).map(v => v.companyName).filter(Boolean))];
        setCompanies(uniqueCompanyNames.sort());
        setCities(cityRes.data || []);
        setStates(stateRes.data || []);
        setCountries(countryRes.data || []);
      }).catch(error => console.error("Error fetching filter data:", error))
        .finally(() => setLoadingFilters(false));
    }
  }, [getAuthHeader, userProfile, canUseAdvancedFilters]); // Added canUseAdvancedFilters dependency


  // --- Filter Handling ---
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Trigger search when filters change (debounced)
  useEffect(() => {
    if (onSearch && canUseAdvancedFilters) {
      const timerId = setTimeout(() => { onSearch(filters); }, 500);
      return () => clearTimeout(timerId);
    } else if (!canUseAdvancedFilters && Object.values(filters).some(v => v !== '' && v !== 'statusFilter')) { // Ignore statusFilter for non-advanced users check
      setFilters({ vendorFilter: '', companyFilter: '', city: '', state: '', country: '', statusFilter: '' }); // Reset all filters
      if (onSearch) onSearch({});
    }
  }, [filters, onSearch, canUseAdvancedFilters]);


  // --- CRUD Handlers ---
  const handleFacilityCreated = (newFacility) => {
    // Parent (Settings.js) will handle refetching the sites list.
    // Consider calling onPaginationChange or a dedicated refresh function if needed.
    // For now, per instructions, removing direct list manipulation.
    // if (setParentSites) setParentSites(prev => [...(prev || []), newFacility]);
    // setDisplayedSites(prev => [...prev, newFacility]); // Removed
    if (onPaginationChange) { // Trigger a refresh by calling onPaginationChange with current page
        onPaginationChange(page, rowsPerPage);
    }
  };

  const handleEditSite = (site) => {
    if (!canEditDelete) return;

    // Define default structure for a single day
    const dayDefault = { isOpen: false, open: '', close: '' };
    // Define default structure for the entire schedule
    const scheduleDefaults = {
      open247: false,
      sameEveryDay: false,
      sameTimeSelectedDays: false, // Add default for the new field
      everyDayOpen: '',
      everyDayClose: '',
      days: {
        monday: { ...dayDefault },
        tuesday: { ...dayDefault },
        wednesday: { ...dayDefault },
        thursday: { ...dayDefault },
        friday: { ...dayDefault },
        saturday: { ...dayDefault },
        sunday: { ...dayDefault }
      }
    };

    // Start with defaults
    let finalSchedule = JSON.parse(JSON.stringify(scheduleDefaults)); // Deep copy defaults

    // If site has a schedule, merge it carefully
    if (site.schedule) {
      // Merge top-level boolean/string fields, checking type
      finalSchedule.open247 = typeof site.schedule.open247 === 'boolean' ? site.schedule.open247 : scheduleDefaults.open247;
      finalSchedule.sameEveryDay = typeof site.schedule.sameEveryDay === 'boolean' ? site.schedule.sameEveryDay : scheduleDefaults.sameEveryDay;
      finalSchedule.sameTimeSelectedDays = typeof site.schedule.sameTimeSelectedDays === 'boolean' ? site.schedule.sameTimeSelectedDays : scheduleDefaults.sameTimeSelectedDays; // Merge new field
      finalSchedule.everyDayOpen = site.schedule.everyDayOpen || scheduleDefaults.everyDayOpen;
      finalSchedule.everyDayClose = site.schedule.everyDayClose || scheduleDefaults.everyDayClose;

      // Merge days individually, ensuring all properties exist
      if (site.schedule.days) {
        Object.keys(finalSchedule.days).forEach(day => {
          if (site.schedule.days[day]) {
            finalSchedule.days[day] = {
              isOpen: typeof site.schedule.days[day].isOpen === 'boolean' ? site.schedule.days[day].isOpen : dayDefault.isOpen,
              open: site.schedule.days[day].open || dayDefault.open,
              close: site.schedule.days[day].close || dayDefault.close,
            };
          }
        });
      }
    }

    // Ensure coveredZipCodes is an array
    const finalCoveredZipCodes = Array.isArray(site.coveredZipCodes) ? site.coveredZipCodes : [];

    // Ensure timezone exists, default if necessary
    const finalTimezone = site.timezone || moment.tz.guess();

    // Set the state with the fully formed site object including the merged schedule, zip codes, and timezone
    setSelectedSite({
      ...site,
      schedule: finalSchedule,
      coveredZipCodes: finalCoveredZipCodes,
      timezone: finalTimezone,
      mcNumber: site.mcNumber || '', // Ensure mcNumber exists
      dotNumber: site.dotNumber || '' // Ensure dotNumber exists
    });
 
    // Removed setMapCoordinates call; JobMapComponent uses locationData prop derived from selectedSite
    const lat = site.location?.coordinates?.[1];
    const lng = site.location?.coordinates?.[0];
    // setMapCoordinates({ lat: typeof lat === 'number' ? lat : 39.8283, lng: typeof lng === 'number' ? lng : -98.5795 }); // Removed this line

    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedSite(null);
    // No manual map cleanup needed for JobMapComponent
  };

  // Handler for schedule changes within the dialog
  const handleScheduleChange = (event) => {
    // Define defaults here as well for fallback if needed
    const dayDefault = { isOpen: false, open: '', close: '' };
    const scheduleDefaults = {
      open247: false, sameEveryDay: false, sameTimeSelectedDays: false,
      everyDayOpen: '', everyDayClose: '',
      days: {
        monday: { ...dayDefault }, tuesday: { ...dayDefault }, wednesday: { ...dayDefault },
        thursday: { ...dayDefault }, friday: { ...dayDefault }, saturday: { ...dayDefault }, sunday: { ...dayDefault }
      }
    };

    const { name, value, type, checked } = event.target;
    const [field, day, subField] = name.split('.'); // e.g., "days.monday.open" or "sameTimeSelectedDays"

    setSelectedSite(prev => {
      // Ensure prev.schedule exists, if not, initialize with defaults
      const currentSchedule = prev.schedule || JSON.parse(JSON.stringify(scheduleDefaults));
      const newSchedule = { ...currentSchedule };

      // Handle top-level checkboxes
      if (field === 'open247' || field === 'sameEveryDay' || field === 'sameTimeSelectedDays') {
        newSchedule[field] = checked;

        // Mutual Exclusivity Logic:
        if (checked) {
          if (field === 'open247') {
            newSchedule.sameEveryDay = false;
            newSchedule.sameTimeSelectedDays = false;
            // Clear other times
            newSchedule.everyDayOpen = '';
            newSchedule.everyDayClose = '';
            Object.keys(newSchedule.days).forEach(d => { newSchedule.days[d] = { isOpen: false, open: '', close: '' }; });
          } else if (field === 'sameEveryDay') {
            newSchedule.open247 = false;
            newSchedule.sameTimeSelectedDays = false;
            // Clear individual day times
            Object.keys(newSchedule.days).forEach(d => { newSchedule.days[d] = { isOpen: false, open: '', close: '' }; });
          } else if (field === 'sameTimeSelectedDays') {
            newSchedule.open247 = false;
            newSchedule.sameEveryDay = false;
             // Clear individual day times (but keep isOpen status)
            Object.keys(newSchedule.days).forEach(d => {
                newSchedule.days[d].open = '';
                newSchedule.days[d].close = '';
            });
          }
        } else {
           // If unchecking, potentially clear related fields
           if (field === 'sameEveryDay') {
               newSchedule.everyDayOpen = '';
               newSchedule.everyDayClose = '';
           } else if (field === 'sameTimeSelectedDays') {
               // When unchecking this, should we clear everydayOpen/Close? Or leave them? Let's clear them.
               newSchedule.everyDayOpen = '';
               newSchedule.everyDayClose = '';
               // Also ensure individual days are potentially ready to be edited
           }
        }
      }
      // Handle "Every Day" time inputs (only relevant if sameEveryDay or sameTimeSelectedDays is true)
      else if (field === 'everyDayOpen' || field === 'everyDayClose') {
        if (newSchedule.sameEveryDay || newSchedule.sameTimeSelectedDays) {
          newSchedule[field] = value;
        }
      }
      // Handle individual day inputs (only relevant if not open247, sameEveryDay, or sameTimeSelectedDays)
      else if (field === 'days' && day && subField) {
        if (!newSchedule.open247 && !newSchedule.sameEveryDay) {
           if (!newSchedule.days[day]) newSchedule.days[day] = { isOpen: false, open: '', close: '' }; // Ensure day object exists

           newSchedule.days[day][subField] = (type === 'checkbox' ? checked : value);

           // If closing a day (isOpen becomes false), clear times for that day
           if (subField === 'isOpen' && !checked) {
               newSchedule.days[day].open = '';
               newSchedule.days[day].close = '';
           }
           // If setting individual day times, ensure sameTimeSelectedDays is false
           if (subField === 'open' || subField === 'close') {
               newSchedule.sameTimeSelectedDays = false;
           }
           // If opening a day, ensure sameTimeSelectedDays is false if it wasn't already handled
           if (subField === 'isOpen' && checked) {
                newSchedule.sameTimeSelectedDays = false;
           }
        }
      }

      // Final cleanup based on flags
      if (newSchedule.open247) {
          newSchedule.everyDayOpen = ''; newSchedule.everyDayClose = '';
          Object.keys(newSchedule.days).forEach(d => { newSchedule.days[d] = { isOpen: false, open: '', close: '' }; });
      } else if (newSchedule.sameEveryDay) {
          Object.keys(newSchedule.days).forEach(d => { newSchedule.days[d] = { isOpen: false, open: '', close: '' }; });
      } else if (newSchedule.sameTimeSelectedDays) {
           // Keep isOpen, clear individual times
           Object.keys(newSchedule.days).forEach(d => {
               newSchedule.days[d].open = ''; newSchedule.days[d].close = '';
           });
      } else { // Individual days logic applies
          newSchedule.everyDayOpen = ''; newSchedule.everyDayClose = '';
      }


      return { ...prev, schedule: newSchedule };
    });
  };


  // --- Temporary provider sign-up invitation ---
  const [inviting, setInviting] = useState(false);

  const getApplicationFormUrl = (site) =>
    `${window.location.origin}/applicationform?vendorId=${encodeURIComponent(site?.vendorNumber || '')}`;

  const handleInviteProvider = async () => {
    if (!selectedSite?._id) return;
    setInviting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`/api/facilities/${selectedSite._id}/invite-provider`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showGlobalNotification(res.data.message || 'Invitation sent.', 'success');
    } catch (err) {
      // Email service down: the backend still returns the link so it can be shared manually
      const data = err.response?.data;
      if (data?.applicationUrl) {
        showGlobalNotification(`${data.message} Link: ${data.applicationUrl}`, 'warning');
      } else {
        showGlobalNotification(data?.message || 'Failed to send invitation.', 'error');
      }
    } finally {
      setInviting(false);
    }
  };

  const handleSaveSite = async () => {
    if (!selectedSite || !selectedSite._id || !canEditDelete) return;
    const headers = getAuthHeader();
    if (!headers) return;
    // Ensure coveredZipCodes is an array before saving
    const zipCodesArray = Array.isArray(selectedSite.coveredZipCodes)
        ? selectedSite.coveredZipCodes.map(zip => zip.trim()).filter(zip => zip) // Ensure trimmed and no empty strings
        : [];
    // Remove lat/lng from dataToSave, JobMapComponent doesn't directly update parent state with coords
    const dataToSave = { ...selectedSite, coveredZipCodes: zipCodesArray };
    // Destructure fields needed for the payload, including services
    const { facilityName, address1, address2, city, state, zip, contactName, contactPhone, contactEmail, schedule, coveredZipCodes, status, services, mcNumber, dotNumber } = dataToSave;
    // Construct payload, including services, mcNumber, and dotNumber
    const payload = { facilityName, address1, address2, city, state, zip, contactName, contactPhone, contactEmail, schedule, coveredZipCodes, services, mcNumber, dotNumber, ...(canChangeStatus && { status }) };
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/facilities/${selectedSite._id}`, payload, { headers });
      // const updatedSite = response.data; // Parent will refetch
      // if (setParentSites) setParentSites(prev => prev.map(s => s._id === updatedSite._id ? updatedSite : s)); // Removed
      // setDisplayedSites(prev => prev.map(s => s._id === updatedSite._id ? updatedSite : s)); // Removed
      handleCloseEditDialog();
      if (onPaginationChange) { // Trigger a refresh
        onPaginationChange(page, rowsPerPage);
      }
    } catch (error) { console.error("Error saving site:", error); showGlobalNotification("Failed to save site changes.", 'error'); }
  };

  const handleDeleteClick = (siteId) => {
    if (!canEditDelete || !siteId) return;
    setSiteToDelete(siteId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!canEditDelete || !siteToDelete) return;
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/facilities/${siteToDelete}`, { headers });
      if (onPaginationChange) {
        onPaginationChange(page, rowsPerPage);
      }
      showGlobalNotification("Site deleted successfully.", 'success');
    } catch (error) {
      console.error("Error deleting site:", error);
      showGlobalNotification("Failed to delete site.", 'error');
    } finally {
      setDeleteDialogOpen(false);
      setSiteToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSiteToDelete(null);
  };

  // --- Status Change Logic ---
   const handleOpenStatusDialog = (site) => {
       if (!canChangeStatus) return; // Check permission
       setSelectedSite({ ...site }); // Set the site for the dialog
       setOpenStatusDialog(true);
   };

   const handleCloseStatusDialog = () => {
       setOpenStatusDialog(false);
       setSelectedSite(null);
   };

   const handleStatusSave = (updatedSite) => {
       // Parent (Settings.js) will handle refetching the sites list.
       // if (setParentSites) { // Removed
       //     setParentSites(prev => prev.map(s => s._id === updatedSite._id ? updatedSite : s));
       // }
       // setDisplayedSites(prev => prev.map(s => s._id === updatedSite._id ? updatedSite : s)); // Removed
       handleCloseStatusDialog();
       if (onPaginationChange) { // Trigger a refresh
           onPaginationChange(page, rowsPerPage);
       }
   };


 // --- Removed Map Logic for Edit Dialog (handleDetectLocation, handleMapClick) ---
  // JobMapComponent handles its own interactions and geocoding.


  return (
    <Box mt={2}>
      <Paper elevation={3} sx={{ padding: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Sites (Facilities)</Typography>
            {/* Use canCreate permission */}
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenCreateDialog(true)} > Create Site </Button>
            )}
        </Box>
        <Divider sx={{ my: 2 }} />
        {readOnly && <ViewOnlyNotice />}

        {/* Filter Section */}
        {canUseAdvancedFilters && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
             <Grid container spacing={2} alignItems="center">
                 {/* Existing Filters */}
                 <Grid item xs={12} sm={6} md={4} lg={2}> <FormControl fullWidth size="small" disabled={loadingFilters}> <InputLabel>Vendor</InputLabel> <Select name="vendorFilter" value={filters.vendorFilter} label="Vendor" onChange={handleFilterChange} > <MenuItem value=""><em>All Vendors</em></MenuItem> {vendors.map((v) => ( <MenuItem key={v.vendorIdentifier} value={v.vendorIdentifier}>{v.vendorIdentifier}</MenuItem> ))} </Select> </FormControl> </Grid>
                 <Grid item xs={12} sm={6} md={4} lg={2}> <FormControl fullWidth size="small" disabled={loadingFilters}> <InputLabel>Company</InputLabel> <Select name="companyFilter" value={filters.companyFilter} label="Company" onChange={handleFilterChange} > <MenuItem value=""><em>All Companies</em></MenuItem> {companies.map((c) => ( <MenuItem key={c} value={c}>{c}</MenuItem> ))} </Select> </FormControl> </Grid>
                 <Grid item xs={12} sm={6} md={4} lg={2}> <FormControl fullWidth size="small" disabled={loadingFilters}> <InputLabel>City</InputLabel> <Select name="city" value={filters.city} label="City" onChange={handleFilterChange} > <MenuItem value=""><em>All Cities</em></MenuItem> {cities.map((c) => ( <MenuItem key={c} value={c}>{c}</MenuItem> ))} </Select> </FormControl> </Grid>
                 <Grid item xs={12} sm={6} md={4} lg={2}> <FormControl fullWidth size="small" disabled={loadingFilters}> <InputLabel>State</InputLabel> <Select name="state" value={filters.state} label="State" onChange={handleFilterChange} > <MenuItem value=""><em>All States</em></MenuItem> {states.map((s) => ( <MenuItem key={s} value={s}>{s}</MenuItem> ))} </Select> </FormControl> </Grid>
                 <Grid item xs={12} sm={6} md={4} lg={2}> <FormControl fullWidth size="small" disabled={loadingFilters}> <InputLabel>Country</InputLabel> <Select name="country" value={filters.country} label="Country" onChange={handleFilterChange} > <MenuItem value=""><em>All Countries</em></MenuItem> {countries.map((c) => ( <MenuItem key={c} value={c}>{c}</MenuItem> ))} </Select> </FormControl> </Grid>
                 {/* Status Filter */}
                 <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            name="statusFilter"
                            value={filters.statusFilter}
                            label="Status"
                            onChange={handleFilterChange}
                        >
                            <MenuItem value=""><em>All Statuses</em></MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                            <MenuItem value="Inactive">Inactive</MenuItem>
                            <MenuItem value="On Hold">On Hold</MenuItem>
                        </Select>
                    </FormControl>
                 </Grid>
             </Grid>
          </Box>
        )}

        {/* Table Structure */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>MC#</TableCell>
                <TableCell>DOT#</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(sites || []).map((site) => (
                <TableRow key={site._id} hover>
                  <TableCell>{site.vendorNumber || 'N/A'}</TableCell>
                  <TableCell>{site.facilityName}</TableCell>
                  <TableCell>{`${site.address1}${site.address2 ? ', ' + site.address2 : ''}, ${site.city}, ${site.state} ${site.zip}`}</TableCell>
                  <TableCell>{site.contactPhone || 'N/A'}</TableCell>
                  <TableCell>{site.mcNumber || 'N/A'}</TableCell>
                  <TableCell>{site.dotNumber || 'N/A'}</TableCell>
                  <TableCell>
                     <Chip
                        label={site.status || 'Unknown'}
                        color={site.status === 'Active' ? 'success' : site.status === 'Pending' ? 'warning' : site.status === 'On Hold' ? 'info' : 'default'}
                        size="small"
                        onClick={canChangeStatus ? () => handleOpenStatusDialog(site) : undefined} // Make chip clickable if user can change status
                        sx={canChangeStatus ? { cursor: 'pointer' } : {}}
                     />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton edge="end" aria-label="edit" onClick={() => handleEditSite(site)} disabled={!canEditDelete || site.status === 'Pending'} > <EditIcon /> </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteClick(site._id)} disabled={!canEditDelete}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount || 0}
          page={page || 0}
          rowsPerPage={rowsPerPage || 10}
          onPageChange={(event, newPage) => onPaginationChange(newPage, rowsPerPage)}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            onPaginationChange(0, newRowsPerPage);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Dialogs */}
      <CreateFacilityDialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} onFacilityCreated={handleFacilityCreated} />

      {/* Edit Site Dialog */}
      {/* ... Edit Dialog JSX remains largely the same ... */}
       <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth >
        <DialogTitle>Edit Site</DialogTitle>
        <DialogContent>
          {selectedSite && (
            <>
              {/* Vendor ID (read-only) + temporary provider sign-up actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Chip label={`Vendor ID: ${selectedSite.vendorNumber || 'N/A'}`} size="small" />
                {selectedSite.isTemporary && <Chip label="TEMPORARY PROVIDER" color="warning" size="small" />}
              </Box>
              {selectedSite.isTemporary && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleInviteProvider}
                    disabled={inviting || !selectedSite.contactEmail}
                  >
                    {inviting ? 'Sending...' : 'Invite Service Provider'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => window.open(getApplicationFormUrl(selectedSite), '_blank', 'noopener')}
                  >
                    View Application Form
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Emails a sign-up invitation with an application link tied to this Vendor ID. Approval replaces this temporary facility and upgrades their login, keeping their job history.
                  </Typography>
                </Box>
              )}
              <TextField margin="dense" label="Facility Name" fullWidth value={selectedSite.facilityName || ''} onChange={(e) => setSelectedSite({ ...selectedSite, facilityName: e.target.value })} />
              <TextField margin="dense" label="Address 1" fullWidth value={selectedSite.address1 || ''} onChange={(e) => setSelectedSite({ ...selectedSite, address1: e.target.value })} />
              <TextField margin="dense" label="Address 2" fullWidth value={selectedSite.address2 || ''} onChange={(e) => setSelectedSite({ ...selectedSite, address2: e.target.value })} />
              <TextField margin="dense" label="City" fullWidth value={selectedSite.city || ''} onChange={(e) => setSelectedSite({ ...selectedSite, city: e.target.value })} />
              <TextField margin="dense" label="State" fullWidth value={selectedSite.state || ''} onChange={(e) => setSelectedSite({ ...selectedSite, state: e.target.value })} />
              <TextField margin="dense" label="Zip" fullWidth value={selectedSite.zip || ''} onChange={(e) => setSelectedSite({ ...selectedSite, zip: e.target.value })} />
              <TextField margin="dense" label="Contact Name" fullWidth value={selectedSite.contactName || ''} onChange={(e) => setSelectedSite({ ...selectedSite, contactName: e.target.value })} />
              <TextField margin="dense" label="Contact Phone" fullWidth value={selectedSite.contactPhone || ''} onChange={(e) => setSelectedSite({ ...selectedSite, contactPhone: e.target.value })} />
              <TextField margin="dense" label="Contact Email" fullWidth type="email" value={selectedSite.contactEmail || ''} onChange={(e) => setSelectedSite({ ...selectedSite, contactEmail: e.target.value })} />
              <TextField margin="dense" label="MC Number" fullWidth value={selectedSite.mcNumber || ''} onChange={(e) => setSelectedSite({ ...selectedSite, mcNumber: e.target.value })} />
              <TextField margin="dense" label="DOT Number" fullWidth value={selectedSite.dotNumber || ''} onChange={(e) => setSelectedSite({ ...selectedSite, dotNumber: e.target.value })} />
              <TextField
                margin="dense"
                label="Covered Zip Codes (comma-separated)"
                fullWidth
                multiline
                rows={2}
                value={Array.isArray(selectedSite.coveredZipCodes) ? selectedSite.coveredZipCodes.join(', ') : ''}
                onChange={(e) => {
                  const zipString = e.target.value;
                  const zipArray = zipString.split(/[\s,]+/).map(zip => zip.trim()).filter(zip => zip);
                  setSelectedSite({ ...selectedSite, coveredZipCodes: zipArray });
                }}
                helperText="Enter zip codes separated by commas or spaces"
              />
              <FormControl fullWidth margin="dense" required>
                <InputLabel id="edit-timezone-select-label">Timezone *</InputLabel>
                <Select
                  labelId="edit-timezone-select-label"
                  id="edit-timezone-select"
                  name="timezone"
                  value={selectedSite.timezone || ''}
                  label="Timezone *"
                  onChange={(e) => setSelectedSite({ ...selectedSite, timezone: e.target.value })}
                >
                  {getFormattedTimezones().map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* --- Schedule Section (remains the same) --- */}
              <Divider sx={{ my: 2 }}><Typography variant="overline">Operating Hours</Typography></Divider>
              {selectedSite?.schedule && (
                <FormGroup>
                  {/* ... schedule checkboxes and inputs ... */}
                   <FormControlLabel control={<Checkbox checked={selectedSite.schedule.open247 || false} onChange={handleScheduleChange} name="open247" />} label="Open 24/7" />
                   {!selectedSite.schedule.open247 && ( <> <FormControlLabel control={<Checkbox checked={selectedSite.schedule.sameEveryDay || false} onChange={handleScheduleChange} name="sameEveryDay" disabled={selectedSite.schedule.sameTimeSelectedDays} />} label="Same Hours For All Open Days" /> <FormControlLabel control={<Checkbox checked={selectedSite.schedule.sameTimeSelectedDays || false} onChange={handleScheduleChange} name="sameTimeSelectedDays" disabled={selectedSite.schedule.sameEveryDay} />} label="Same Time For Selected Days" /> {(selectedSite.schedule.sameEveryDay || selectedSite.schedule.sameTimeSelectedDays) && ( <Grid container spacing={2} sx={{ pl: 4, mb: 1 }}> <Grid item xs={6}> <TextField label="Open Time (for selected)" type="time" name="everyDayOpen" value={selectedSite.schedule.everyDayOpen || ''} onChange={handleScheduleChange} InputLabelProps={{ shrink: true }} fullWidth size="small" /> </Grid> <Grid item xs={6}> <TextField label="Close Time (for selected)" type="time" name="everyDayClose" value={selectedSite.schedule.everyDayClose || ''} onChange={handleScheduleChange} InputLabelProps={{ shrink: true }} fullWidth size="small" /> </Grid> </Grid> )} {!selectedSite.schedule.sameEveryDay && ( <Box sx={{ pl: 4 }}> {Object.keys(selectedSite.schedule.days || {}).map((day) => { return ( <Grid container spacing={1} key={day} alignItems="center" sx={{ mb: 0.5 }}> <Grid item xs={3}> <FormControlLabel control={<Checkbox checked={selectedSite.schedule.days[day]?.isOpen || false} onChange={handleScheduleChange} name={`days.${day}.isOpen`} size="small" />} label={day.charAt(0).toUpperCase() + day.slice(1)} /> </Grid> {!selectedSite.schedule.sameTimeSelectedDays && ( <> <Grid item xs={4}> <TextField label="Open" type="time" name={`days.${day}.open`} value={selectedSite.schedule.days[day]?.open || ''} onChange={handleScheduleChange} InputLabelProps={{ shrink: true }} fullWidth size="small" disabled={!selectedSite.schedule.days[day]?.isOpen} /> </Grid> <Grid item xs={4}> <TextField label="Close" type="time" name={`days.${day}.close`} value={selectedSite.schedule.days[day]?.close || ''} onChange={handleScheduleChange} InputLabelProps={{ shrink: true }} fullWidth size="small" disabled={!selectedSite.schedule.days[day]?.isOpen} /> </Grid> </> )} {selectedSite.schedule.sameTimeSelectedDays && ( <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', pl: 1 }}> {selectedSite.schedule.days[day]?.isOpen && ( <Typography variant="caption" color="text.secondary"> ({selectedSite.schedule.everyDayOpen || 'N/A'} - {selectedSite.schedule.everyDayClose || 'N/A'}) </Typography> )} </Grid> )} </Grid> ); })} </Box> )} </> )}
                </FormGroup>
              )}
              {/* --- End Schedule Section --- */}

              {/* --- Services Section --- */}
              <Divider sx={{ my: 2 }}><Typography variant="overline">Services</Typography></Divider>
              <FormGroup>
                {/* Roadside Services */}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Roadside Services
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {[
                    { key: 'roadsideJumpStartsLightDuty', label: 'Jump Start - Light Duty' },
                    { key: 'roadsideJumpStartsMediumDuty', label: 'Jump Start - Medium Duty' },
                    { key: 'roadsideJumpStartsHeavyDuty', label: 'Jump Start - Heavy Duty' },
                    { key: 'roadsideTireChangesLightDuty', label: 'Tire Change - Light Duty' },
                    { key: 'roadsideTireChangesMediumDuty', label: 'Tire Change - Medium Duty' },
                    { key: 'roadsideTireChangesHeavyDuty', label: 'Tire Change - Heavy Duty' },
                    { key: 'roadsideLockOutsLightDuty', label: 'Lock Out - Light Duty' },
                    { key: 'roadsideLockOutsMediumDuty', label: 'Lock Out - Medium Duty' },
                    { key: 'roadsideLockOutsHeavyDuty', label: 'Lock Out - Heavy Duty' },
                    { key: 'roadsideFuelDeliveriesLightDuty', label: 'Fuel Delivery - Light Duty' },
                    { key: 'roadsideFuelDeliveriesMediumDuty', label: 'Fuel Delivery - Medium Duty' },
                    { key: 'roadsideFuelDeliveriesHeavyDuty', label: 'Fuel Delivery - Heavy Duty' },
                    { key: 'winchOutsLightDuty', label: 'Winch Out - Light Duty' },
                    { key: 'winchOutsMediumDuty', label: 'Winch Out - Medium Duty' },
                    { key: 'winchOutsHeavyDuty', label: 'Winch Out - Heavy Duty' }
                  ].map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={selectedSite.services?.[key] || false}
                          onChange={(e) => {
                            setSelectedSite({
                              ...selectedSite,
                              services: {
                                ...selectedSite.services,
                                [key]: e.target.checked
                              }
                            });
                          }}
                        />
                      }
                      label={label}
                    />
                  ))}
                </Box>

                {/* Towing Services */}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Towing Services
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {[
                    { key: 'lightDutyTowing', label: 'Light Duty Towing' },
                    { key: 'mediumDutyTowing', label: 'Medium Duty Towing' },
                    { key: 'heavyDutyTowing', label: 'Heavy Duty Towing' },
                    { key: 'accidentSceneTowing', label: 'Accident Scene Towing' },
                    { key: 'secondaryTow', label: 'Secondary Tow' }
                  ].map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={selectedSite.services?.[key] || false}
                          onChange={(e) => {
                            setSelectedSite({
                              ...selectedSite,
                              services: {
                                ...selectedSite.services,
                                [key]: e.target.checked
                              }
                            });
                          }}
                        />
                      }
                      label={label}
                    />
                  ))}
                </Box>

                {/* Other Services */}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Other Services
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {[
                    { key: 'mobileMechanic', label: 'Mobile Mechanic' },
                    { key: 'mediumHeavyTire', label: 'Medium Heavy Tire' },
                    { key: 'storageFacility', label: 'Storage Facility' },
                    { key: 'keyMaking', label: 'Key Making' },
                    { key: 'locksmith', label: 'Locksmith' },
                    { key: 'electricCharging', label: 'Electric Charging' }
                  ].map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={selectedSite.services?.[key] || false}
                          onChange={(e) => {
                            setSelectedSite({
                              ...selectedSite,
                              services: {
                                ...selectedSite.services,
                                [key]: e.target.checked
                              }
                            });
                          }}
                        />
                      }
                      label={label}
                    />
                  ))}
                </Box>
              </FormGroup>
              {/* --- End Services Section --- */}

              <Divider sx={{ my: 2 }}><Typography variant="overline">Location</Typography></Divider>
              {/* Use JobMapComponent */}
              <Box mt={2} style={{ height: '300px', border: '1px solid #ccc' }}>
                {memoizedLocationData && <JobMapComponent locationData={memoizedLocationData} />}
              </Box>
          
              {/* Delete Confirmation Dialog */}
              <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Site"
                content="Are you sure you want to delete this site? This action cannot be undone."
                confirmColor="error"
              />
              {/* Removed map click caption and detect location button */}
            </>
          )}
        </DialogContent>
        <DialogActions> <Button onClick={handleCloseEditDialog}>Cancel</Button> <Button onClick={handleSaveSite} color="primary"> Save </Button> </DialogActions>
      </Dialog>

       {/* Status Change Dialog */}
       {selectedSite && (
           <ChangeFacilityStatusDialog
               open={openStatusDialog}
               onClose={handleCloseStatusDialog}
               facility={selectedSite}
               onStatusSave={handleStatusSave} // Pass handler to update state on successful save
           />
       )}

    </Box>
  );
}

export default Sites;
